import { Injectable, inject } from '@angular/core';
import { of } from 'rxjs';
import {
  switchMap,
  map,
  catchError,
  filter,
  withLatestFrom,
  pairwise,
  mergeMap,
  take,
  tap,
  first,
  delay
} from 'rxjs/operators';
import { Clipboard } from '@angular/cdk/clipboard';
import { select, Store } from '@ngrx/store';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import {
  eachHourOfInterval,
  subHours,
  addHours,
  subMinutes
} from 'date-fns/esm';
import difference from 'lodash-es/difference';

import { ofRoute, selectZefNgrxRouterIdByKey, zefGo } from '@zerops/zef/ngrx-router';
import { log } from '@zerops/zef/core';
import {
  onWebsocketMessageDispatchUpdateEntities,
  MergeStrategy,
  onWebsocketMessageDispatchAddRemoveEntities,
  successOf,
  errorOf,
  getSubscriptionNameForFeature,
  selectEntityList,
  EntityOps,
} from '@zerops/zef/entities';
import { ZefSnackService } from '@zerops/zef/snack';
import { zefDialogClose } from '@zerops/zef/dialog';
import { onWebsocketSubscriptionName } from '@zerops/zef/websocket';
import { ZefEntityActionOptions } from '@zerops/zef/entities/entity-manager.model';
import { requestShowYaml } from '@zerops/zui/recipes/recipes.action';
import { Project } from '@zerops/models/project';
import {
  TransactionDebitGroupItem,
  TransactionGroupBy,
  TRANSACTION_GROUP_RANGE
} from '@zerops/models/transaction-debit';
import { Subscription } from '@zerops/models/subscription';
import { transactionDebitGroupPrefillData } from '@zerops/zerops/core/transaction-debit-base';
import { deletionWarningDialogOpen } from '@zerops/zerops/feature/deletion-warning-dialog';
import { ADDON_ACTIVATION_DIALOG_FEATURE_NAME } from '@zerops/zerops/feature/addon-activation-dialog';
import {
  ImportExportDialogModes,
  importExportDialogOpen,
  IMPORT_EXPORT_DIALOG_FEATURE_NAME
} from '@zerops/zerops/feature/import-export-dialog';
import { ApiEntityKeys, AppState, RouteKeys } from '@zerops/zerops/app';
import { UserEntity } from '../user-base';
import { SubscriptionEntity } from '../subscription-base';
import { availableAddons } from '../billing-base';
import { firstClassRecipeImport } from '../service-stack-base';
import { ZeropsYamlBuilder, ZagentMiniConfig } from '../zerops-services';
import {
  ZCP_RECIPE_SOURCE,
  FEATURE_NAME as CONTROL_PLANE_FEATURE_NAME
} from '@zerops/zerops/feature/control-plane-dialog';
import { modeToDefaults } from '../zerops-services';
import {
  loadProjectTags,
  loadProjectTagsSuccess,
  loadProjectTagsFail,
  requestIPv4Success,
  requestIPv4,
  requestIPv4Fail,
  startProject,
  startProjectSuccess,
  startProjectFail,
  projectExport,
  projectExportSuccess,
  projectExportFail,
  projectImport,
  projectImportSuccess,
  projectImportFail,
  updateRemoteLogging,
  updateRemoteLoggingSuccess,
  updateRemoteLoggingFail,
  addVpnPublicKey,
  addVpnPublicKeySuccess,
  addVpnPublicKeyFail,
  deleteVpnPublicKey,
  deleteVpnPublicKeySuccess,
  deleteVpnPublicKeyFail,
  listVpnPublicKeys,
  listVpnPublicKeysSuccess,
  listVpnPublicKeysFail,
  addProjectCustomEnv,
  addProjectCustomEnvSuccess,
  addProjectCustomEnvFail,
  updateProjectCustomEnvs,
  updateProjectCustomEnvsSuccess,
  updateProjectCustomEnvsFail,
  updateProjectEnv,
  updateProjectEnvSuccess,
  updateProjectEnvFail,
  deleteProjectCustomEnv,
  deleteProjectCustomEnvSuccess,
  deleteProjectCustomEnvFail,
  loadProjectEnvFile,
  loadProjectEnvFileSuccess,
  loadProjectEnvFileFail,
  revealProjectEnv,
  revealProjectEnvSuccess,
  revealProjectEnvFail,
  updateProjectMode,
  updateProjectModeSuccess,
  updateProjectModeFail,
  loadL7HttpBalancerConfig,
  loadL7HttpBalancerConfigSuccess,
  loadL7HttpBalancerConfigFail,
  updateL7HttpBalancerConfig,
  updateL7HttpBalancerConfigSuccess,
  updateL7HttpBalancerConfigFail,
  updateIPv4Shared,
  updateIPv4SharedSuccess,
  updateIPv4SharedFail
} from './project-base.action';
import { FEATURE_NAME } from './project-base.constant';
import { ProjectEntity } from './project-base.entity';
import { ProjectBaseApi } from './project-base.api';

interface MessageAddRemoveIdsCache {
  data: string[];
  meta?: Partial<ZefEntityActionOptions>;
  op: EntityOps.AddIdsToCache | EntityOps.RemoveIdsFromCache;
  entityName: string;
  totalHits?: number;
  type: string;
}

@Injectable()
export class ProjectBaseEffect {
  #actions$ = inject(Actions);
  #api = inject(ProjectBaseApi);
  #projectEntity = inject(ProjectEntity);
  #userEntity = inject(UserEntity);
  #subscriptionEntity = inject(SubscriptionEntity);
  #snack = inject(ZefSnackService);
  #clipboard = inject(Clipboard);
  #store = inject(Store<AppState>);
  #yamlBuilder = inject(ZeropsYamlBuilder);

  #clientId$ = this.#userEntity.activeClientId$;
  #subscriptions$ = this.#subscriptionEntity.list$();

  onLoadTags$ = createEffect(() => this.#actions$.pipe(
    ofType(loadProjectTags),
    switchMap((action) => this.#api
      .loadTags$(action.data)
      .pipe(
        map((res) => loadProjectTagsSuccess(res.items, action)),
        catchError((err) => of(loadProjectTagsFail(err, action)))
      )
    )
  ));

  setupListStreamSubscription$ = createEffect(() => this.#clientId$.pipe(
    map((clientId) => this.#projectEntity.listSubscribe(
      clientId,
      FEATURE_NAME,
      undefined,
      {
        handleGlobally: false
      }
    ))
  ));

  prefillTransactionDebitForProjects$ = createEffect(() => this.#store.pipe(
    // we only need ids, so we are taking them raw from the state
    select(selectEntityList(this.#projectEntity.entityName)),
    pairwise(),
    // checking if there are new ids
    map(([ prevIds, currIds ]) => difference(currIds, prevIds)),
    mergeMap((ids: string[]) => {

      const groupRange = TRANSACTION_GROUP_RANGE.last24h;

      const items = ids.reduce((arr: any, id: string) => {

        arr.push(...eachHourOfInterval({
          start: addHours(groupRange.range.from, 1),
          end: groupRange.range.to
        }).map((itm: Date) => ({
          sumTotalPrice: 0,
          from: subHours(itm, 1),
          projectId: id,
          till: subMinutes(itm, 1)
        })));

        return arr;

      }, []) as TransactionDebitGroupItem[];

      return [
        transactionDebitGroupPrefillData({
          timeGroupBy: groupRange.timeGroupBy,
          groupBy: TransactionGroupBy.Project,
          limit: groupRange.limit,
          from: groupRange.range.from,
          till: groupRange.range.to,
          key: groupRange.key,
          items
        }),
        transactionDebitGroupPrefillData({
          timeGroupBy: groupRange.timeGroupBy,
          groupBy: TransactionGroupBy.Service,
          limit: groupRange.limit,
          from: groupRange.range.from,
          till: groupRange.range.to,
          key: groupRange.key,
          items
        })
      ];

    })
  ));

  setupAddRemoveMessage$ = createEffect(() => this.#actions$.pipe(
    onWebsocketMessageDispatchAddRemoveEntities(this.#projectEntity),
    withLatestFrom(this.#subscriptions$, this.#clientId$),
    mergeMap(([ message, subscriptions, clientId ]: [ MessageAddRemoveIdsCache, Subscription[], string ]) => {

      /**
       * This a temporary solution to trigger the update of available subscriptions/addons after receiving a WS message about project add/remove.
       * The reason is that after adding a new project, the list of available subscriptions/addons can be changed and we need to update it in the store.
       * The better solution will be to handle this logic in the backend by sending a specific WS message about available subscriptions/addons change
       * after a project add/remove and then listen to this message in the frontend to update available subscriptions/addons, but for now it is not available,
       * because the subscription/addon entity (in billing database) is not changed on the BE side when a project is added/removed on a user (business database).
       */

      if (message.op === EntityOps.RemoveIdsFromCache) {
        const projectIds = new Set(message.data);
        const subscriptionsForRemovedProjects = subscriptions.filter(subscription => projectIds.has(subscription.projectId));
        const subscriptionIds = subscriptionsForRemovedProjects.map(subscription => subscription.id);
        return [
          message,
          this.#subscriptionEntity.removeIdsFromCache(subscriptionIds, { handleGlobally: true })
        ];
      }

      if (message.op === EntityOps.AddIdsToCache) {
        return [
          message,
          this.#subscriptionEntity.listSubscribe(
            clientId,
            undefined,
            {
              search: [
                {
                  name: 'status',
                  operator: 'eq',
                  value: 'ACTIVE'
                }
              ]
            }
          )
        ];
      }

      return [ message ];

    })
  ));

  setupUpdateStreamSubscription$ = createEffect(() => this.#userEntity.activeClientId$.pipe(
    map((clientId) => this.#projectEntity.updateSubscribe(clientId),
  )));

  onUpdateStreamMessage$ = createEffect(() => this.#actions$.pipe(
    onWebsocketMessageDispatchUpdateEntities(
      this.#projectEntity,
      {
        zefEntityMergeStrategy: {
          firewallIpRanges: MergeStrategy.KeepNew,
          envList: MergeStrategy.KeepNew,
          userRoles: MergeStrategy.KeepNew
        }
      }
    )
  ));

  onRequestIpv4$ = createEffect(() => this.#actions$.pipe(
    ofType(requestIPv4),
    switchMap((action) => this.#api
      .requestIpv4$(action.data)
      .pipe(
        map((res) => requestIPv4Success(res, action)),
        catchError((res) => of(requestIPv4Fail(res, action)))
      )
    )
  ));

  onProjectAddSuccessLoadAvailableAddons$ = createEffect(() => this.#actions$.pipe(
    successOf<{ success: boolean; }>(this.#projectEntity.addOne),
    map(() => availableAddons())
  ));

  private _onRequestIpv4Success$ = this.#actions$.pipe(
    ofType(requestIPv4Success)
  );

  onRequestIpv4SuccessLoadAvailableAddons$ = createEffect(() => this._onRequestIpv4Success$.pipe(
    map(() => availableAddons())
  ));

  onRequestIpv4SuccessCloseAddonDialog$ = createEffect(() => this._onRequestIpv4Success$.pipe(
    map(() => zefDialogClose({ key: ADDON_ACTIVATION_DIALOG_FEATURE_NAME }))
  ));

  onStartProject$ = createEffect(() => this.#actions$.pipe(
    ofType(startProject),
    switchMap((action) => this.#api
      .startProject$(action.data.id)
      .pipe(
        map((res) => startProjectSuccess(res, action)),
        catchError((res) => of(startProjectFail(res, action)))
      )
    )
  ));

  onGetOneError$ = createEffect(() => this.#actions$.pipe(
    errorOf<Project>(this.#projectEntity.getOne),
    filter(({ meta: { zefError } }) => zefError.code === 'projectNotFound'),
    map(() => deletionWarningDialogOpen({ entity: ApiEntityKeys.Project }))
  ));

  private _onUpdateSuccess$ = this.#actions$.pipe(
    successOf<Project>(this.#projectEntity.updateOne)
  );

  onProjectUpdateSuccessNotification$ = createEffect(() => this._onUpdateSuccess$.pipe(
    filter((action) => !action.originalAction?.meta?.tag),
    switchMap(() => this.#snack.success$({ text: 'Project was updated successfully' }))
  ), { dispatch: false });

  onUpdateSuccessNotification$ = createEffect(() => this._onUpdateSuccess$.pipe(
    filter((action) => action.originalAction?.meta?.tag === 'limit'),
    switchMap(() => this.#snack.success$({ text: 'Project daily cost limit was updated successfully' }))
  ), { dispatch: false });

  onRemoveLimitSuccessNotification$ = createEffect(() => this._onUpdateSuccess$.pipe(
    filter((action) => action.originalAction?.meta?.tag === 'limit_remove'),
    switchMap(() => this.#snack.success$({ text: 'Project daily cost limit was removed successfully' }))
  ), { dispatch: false });

  onProjectExport$ = createEffect(() => this.#actions$.pipe(
    ofType(projectExport),
    switchMap((action) => this.#api
      .projectExport$(action.data.id, action.data.reveal)
      .pipe(
        map((res) => projectExportSuccess(res.yaml, action)),
        catchError((res) => of(projectExportFail(res, action)))
      )
    )
  ));

  onProjectExportSuccess$ = createEffect(() => this.#actions$.pipe(
    ofType(projectExportSuccess),
    map(({ data, originalAction }) => importExportDialogOpen({
      yaml: data,
      mode: ImportExportDialogModes.Export,
      entity: 'project',
      exportId: originalAction?.data?.id,
      reveal: !!originalAction?.data?.reveal,
      canRevealSecrets: !!originalAction?.data?.canRevealSecrets
    }))
  ));

  onProjectImport$ = createEffect(() => this.#actions$.pipe(
    ofType(projectImport),
    withLatestFrom(this.#clientId$),
    mergeMap(([ action, clientId ]) => this.#api
      .projectImport$(action.data, clientId)
      .pipe(
        map((res) => projectImportSuccess(
          { projectId: res.projectId, projectName: res.projectName },
          action
        )),
        catchError((res) => of(projectImportFail(res, action)))
      )
    )
  ));

  private _onProjectImportSuccess$ = this.#actions$.pipe(
    ofType(projectImportSuccess)
  );

  onProjectImportSuccessRedirect$ = createEffect(() => this._onProjectImportSuccess$.pipe(
    filter(({ originalAction }) => !!originalAction.meta?.redirect),
    map(({ originalAction }) => zefGo(originalAction.meta?.redirect))
  ));

  onProjectImportSuccessDialogClose$ = createEffect(() => this._onProjectImportSuccess$.pipe(
    map(() => zefDialogClose({
      key: IMPORT_EXPORT_DIALOG_FEATURE_NAME
    }))
  ));

  onProjectImportSuccessUpdate$ = createEffect(() => this._onProjectImportSuccess$.pipe(
    withLatestFrom(this.#clientId$),
    delay(100),
    map(([ _, clientId ]) => this.#projectEntity.listSubscribe(
      clientId,
      FEATURE_NAME
    ))
  ));

  /**
   * Chain control plane deployment after project import success when zagentConfig is present in meta.
   * Generates YAML from config and dispatches firstClassRecipeImport.
   * Coding Agent + Cloud IDE are independent opt-in features on the unified zcp service.
   * Delay allows project to be fully ready before deploying control plane service.
   */
  onProjectImportSuccessWithZagent$ = createEffect(() => this._onProjectImportSuccess$.pipe(
    filter(({ originalAction }) => !!originalAction?.meta?.zagentConfig),
    delay(500),
    map(({ data, originalAction }) => {
      const config = originalAction.meta.zagentConfig as ZagentMiniConfig;
      const fallback = modeToDefaults(config.mode);
      const enableAgent = config.enableAgent ?? fallback.enableAgent;
      const enableVscode = config.enableVscode ?? config.includeVscode ?? fallback.enableVscode;

      const agents = !enableAgent
        ? []
        : (config.agents?.length
          ? config.agents.map((a) => ({
            agentType: a.agentType,
            authType: a.authType,
            apiToken: a.apiToken
          }))
          : (config.agentType
            ? [ {
              agentType: config.agentType,
              authType: config.authType,
              apiToken: config.apiToken
            } ]
            : []));

      const yaml = this.#yamlBuilder.generate({
        serviceName: 'zcp',
        enableAgent,
        enableVscode,
        agents,
        enablePublicAccess: config.enablePublicAccess,
        vscodePassword: config.vscodePassword,
        selectedServices: config.selectedServices
      });

      return firstClassRecipeImport({
        data: {
          projectId: data.projectId,
          serviceImportYaml: yaml,
          recipeSource: ZCP_RECIPE_SOURCE,
          recipeSourceUrl: undefined,  // Platform-internal recipes have no URL
          createIntegrationToken: enableAgent,
          // Carry the OAuth-mode agent list so the auth-dialog effect can
          // seed its walker queue at deploy time without reading userData.
          zcpAgents: agents.filter((a) => a.authType === 'oauth').map((a) => a.agentType)
        },
        meta: {
          skipNotification: true,
          source: CONTROL_PLANE_FEATURE_NAME,
          authType: config.authType,
          agentType: config.agentType
        }
      });
    })
  ));

  onRequestShowYaml$ = createEffect(() => this.#actions$.pipe(
    ofType(requestShowYaml),
    map(({ yaml }) => importExportDialogOpen({ mode: ImportExportDialogModes.Import, yaml }))
  ));

  listSubscription$ = createEffect(() => this.#actions$.pipe(
    onWebsocketSubscriptionName(getSubscriptionNameForFeature(
      ApiEntityKeys.Project,
      'list'
    )),
    filter((message) => message.data.add && message.data.add.length),
    map(() => availableAddons())
  ));

  onUpdateRemoteLoggingRequest$ = createEffect(() => this.#actions$.pipe(
    ofType(updateRemoteLogging),
    switchMap((action) => this.#api
      .updateRemoteLogging$(action.data.id, action.data.payload)
      .pipe(
        map((res) => updateRemoteLoggingSuccess(res, action)),
        catchError((res) => of(updateRemoteLoggingFail(res, action)))
      )
    )
  ));

  /**
   * Is invoked by the action to add a new WireGuard VPN public key.
   */
  onAddVpnPublicKeyRequest$ = createEffect(() => this.#actions$.pipe(
    ofType(addVpnPublicKey),
    switchMap((action) => this.#api
      .addVpnPublicKey$(action.data.id, action.data.payload)
      .pipe(
        map((res) => addVpnPublicKeySuccess(res, action)),
        catchError((res) => of(addVpnPublicKeyFail(res, action)))
      )
    )
  ));

  /**
   * Is invoked by the action to delete already existed WireGuard VPN public key.
   */
  onDeleteVpnPublicKeyRequest$ = createEffect(() => this.#actions$.pipe(
    ofType(deleteVpnPublicKey),
    switchMap((action) => this.#api
      .deleteVpnPublicKey$(action.data.id, action.data.payload)
      .pipe(
        map((res) => deleteVpnPublicKeySuccess(res, action)),
        catchError((res) => of(deleteVpnPublicKeyFail(res, action)))
      )
    )
  ));

  /**
   * Is invoked by the action to list already existed WireGuard VPN public keys.
   */
  onListVpnPublicKeysRequest$ = createEffect(() => this.#actions$.pipe(
    ofType(listVpnPublicKeys),
    switchMap((action) => this.#api
      .listVpnPublicKeys$(action.data.id)
      .pipe(
        map((res) => listVpnPublicKeysSuccess(
          {
            id: action.data.id,
            response: res
          },
        action
        )),
        catchError((res) => of(listVpnPublicKeysFail(res, action)))
      )
    )
  ));

  /**
   * The successful addition or deletion of WireGuard VPN public keys invokes it.
   * The reason is to refresh data in the store because they are not updated via WS.
   */
  onVpnPublicKeysChangeRequest$ = createEffect(() => this.#actions$.pipe(
    ofType(addVpnPublicKeySuccess, deleteVpnPublicKeySuccess),
    map((action) => listVpnPublicKeys({ id: action.originalAction.data.id }))
  ));

  /**
   * Used to initially load all registered WireGuard VPN public keys
   * when a user navigates to the project detail route.
   * This action is called also after each request for adding a new
   * or deleting existed public key to refresh the store.
   */
  onRoute$ = createEffect(() => this.#actions$.pipe(
    ofRoute('project/:id/routing'),
    switchMap(() => this.#store.pipe(
      select(selectZefNgrxRouterIdByKey(RouteKeys.ProjectDetail)),
      filter((d) => !!d),
      take(1)
    )),
    map((id) => listVpnPublicKeys({ id }))
  ));

  /**
   * Is invoked by the action to add a new project custom environment variable.
   */
  onAddProjectCustomEnvRequest$ = createEffect(() => this.#actions$.pipe(
    ofType(addProjectCustomEnv),
    mergeMap((action) => this.#api
      .addProjectCustomEnv$(action.data.projectId, action.data.payload)
      .pipe(
        map((res) => addProjectCustomEnvSuccess(res, action)),
        catchError((res) => of(addProjectCustomEnvFail(res, action)))
      )
    )
  ));

  /**
   * Is invoked by the action to update all project custom environment variables.
   * It is used a stringified list of all variables as a content.
   */
  onUpdateProjectCustomEnvsRequest$ = createEffect(() => this.#actions$.pipe(
    ofType(updateProjectCustomEnvs),
    switchMap((action) => this.#api
      .updateProjectCustomEnvs$(action.data.projectId, action.data.content, action.data.sensitive)
      .pipe(
        map((res) => updateProjectCustomEnvsSuccess(res, action)),
        catchError((res) => of(updateProjectCustomEnvsFail(res, action)))
      )
    )
  ));

  /**
   * Is invoked by the action to update an individual project custom or editable generated environment variable.
   */
  onUpdateProjectEnvRequest$ = createEffect(() => this.#actions$.pipe(
    ofType(updateProjectEnv),
    switchMap((action) => this.#api
      .updateProjectEnv$(action.data.id, action.data.payload)
      .pipe(
        map((res) => updateProjectEnvSuccess(res, action)),
        catchError((res) => of(updateProjectEnvFail(res, action)))
      )
    )
  ));

  /**
   * Is invoked by the action to delete individual project custom environment variable.
   */
  onDeleteProjectCustomEnvRequest$ = createEffect(() => this.#actions$.pipe(
    ofType(deleteProjectCustomEnv),
    switchMap((action) => this.#api
      .deleteProjectCustomEnv$(action.data.id)
      .pipe(
        map((res) => deleteProjectCustomEnvSuccess(res, action)),
        catchError((res) => of(deleteProjectCustomEnvFail(res, action)))
      )
    )
  ));

  /**
   * Reveals the decrypted value of a sensitive project env. If the backend requires
   * sudo, the failure carries a sudo-required error whose originalAction is this
   * reveal action - the global sudo-reauth flow re-dispatches it after re-auth.
   *
   * `mergeMap`, not `switchMap`: reveals are per-item and independently triggered
   * (list actions, click-to-reveal, the multi-secret access-details copy flow fires
   * several at once). Switching would silently cancel an earlier item's request and
   * leave it redacted with no error and no spinner.
   */
  onRevealProjectEnvRequest$ = createEffect(() => this.#actions$.pipe(
    ofType(revealProjectEnv),
    mergeMap((action) => this.#api
      .revealProjectEnv$(action.data.id)
      .pipe(
        map((res) => revealProjectEnvSuccess(res, action)),
        catchError((res) => of(revealProjectEnvFail(res, action)))
      )
    )
  ));

  /**
   * A reveal that came from clicking the masked value means "I want this value", so
   * hand it over once it lands. It has to be a second, explicit gesture: reveal is
   * async and usually puts the sudo re-auth dialog in between, and browsers reject a
   * clipboard write that isn't inside a user gesture.
   */
  onRevealProjectEnvSuccessOfferCopy$ = createEffect(() => this.#actions$.pipe(
    ofType(revealProjectEnvSuccess),
    filter(({ originalAction }) => !!originalAction?.data?.copyOnSuccess),
    switchMap(({ data }) => this.#snack
      .open('Value revealed', 'Copy')
      .onAction()
      .pipe(
        take(1),
        tap(() => this.#clipboard.copy(data.content))
      )
    )
  ), { dispatch: false });

  /**
   * Bulk-reveal for the multi-edit dialog: fetches all project envs of one category
   * as a single `.env` blob. When sensitive, the backend requires sudo, so a failure
   * carries a sudo-required error whose originalAction is this single load action -
   * the global sudo-reauth flow re-dispatches it intact after re-auth.
   */
  onLoadProjectEnvFile$ = createEffect(() => this.#actions$.pipe(
    ofType(loadProjectEnvFile),
    switchMap((action) => this.#api
      .getProjectEnvFile$(action.data.id, action.data.sensitive)
      .pipe(
        map((res) => loadProjectEnvFileSuccess(res, action)),
        catchError((res) => of(loadProjectEnvFileFail(res, action)))
      )
    )
  ));

  /**
   * Is invoked by the success action on delete individual project custom environment variable.
   * Updates a list of project's envs to immediately hide the deleted one without waiting for WS data.
   */
  onDeleteProjectCustomEnvSuccessAction$ = createEffect(() => this.#actions$.pipe(
    ofType(deleteProjectCustomEnvSuccess),
    switchMap(({ data, originalAction }) => this.#projectEntity.entityById$(data.projectId).pipe(
      first(),
      filter((project) => !!project),
      map((project) => this.#projectEntity.updateCache(
        [{
          id: project.id,
          envList: project.envList.filter((env) => env.id !== originalAction.data.id)
        }],
        {
          zefEntityMergeStrategy: {
            envList: MergeStrategy.KeepNew
          }
        }
      ))
    ))
  ));

  onUpdateProjectMode$ = createEffect(() => this.#actions$.pipe(
    ofType(updateProjectMode),
    switchMap(( action ) =>
      this.#api.updateProjectMode$(action.data.projectId, action.data.mode)
      .pipe(
        map((res) => updateProjectModeSuccess(res, action)),
        catchError((res) => of(updateProjectModeFail(res, action)))
      )
    )
  ));

  onLoadL7HttpBalancerConfig$ = createEffect(() => this.#actions$.pipe(
    ofType(loadL7HttpBalancerConfig),
    switchMap((action) => this.#api
      .getL7HttpBalancerConfig$(action.data.projectId)
      .pipe(
        map((res) => loadL7HttpBalancerConfigSuccess({
          id: action.data.projectId,
          response: res
        }, action)),
        catchError((res) => of(loadL7HttpBalancerConfigFail(res, action)))
      )
    )
  ));

  onUpdateL7HttpBalancerConfig$ = createEffect(() => this.#actions$.pipe(
    ofType(updateL7HttpBalancerConfig),
    log('onUpdateL7HttpBalancerConfig$'),
    switchMap((action) => this.#api
      .updateL7HttpBalancerConfig$(action.data.projectId, action.data.payload)
      .pipe(
        map((res) => updateL7HttpBalancerConfigSuccess(res, action)),
        catchError((res) => of(updateL7HttpBalancerConfigFail(res, action)))
      )
    )
  ));

  onUpdateIPv4Shared$ = createEffect(() => this.#actions$.pipe(
    ofType(updateIPv4Shared),
    switchMap(( action ) =>
      this.#api.updateIPv4Shared(action.data.projectId, action.data.enabled)
      .pipe(
        map((res) => updateIPv4SharedSuccess(res, action)),
        catchError((res) => of(updateIPv4SharedFail(res, action)))
      )
    )
  ));

  /**
   * Control Plane Deployment Effect (Centralized) - FIRST CLASS RECIPE IMPORT
   *
   * When a project is created with zcpConfig in meta, automatically deploy
   * the unified zcp control plane service to the new project using
   * firstClassRecipeImport. Coding Agent + Cloud IDE are independent opt-in
   * features driven by `enableAgent` / `enableVscode` on the config.
   *
   * This is a centralized handler - any feature that passes zcpConfig in
   * the addOne meta will trigger control plane deployment. Features only need to:
   * 1. Collect config (via Control Plane dialog)
   * 2. Pass it in meta: projectEntity.addOne(data, { zcpConfig })
   *
   * This effect handles the rest using first-class recipe import.
   */
  onProjectAddSuccessDeployZcp$ = createEffect(() => this.#actions$.pipe(
    successOf<Project>(this.#projectEntity.addOne),
    filter(({ originalAction }) => !!originalAction?.meta?.zcpConfig),
    delay(500),
    map(({ meta, originalAction }) => {
      const projectId = meta?.rawResult?.id;
      const config = originalAction.meta.zcpConfig as ZagentMiniConfig;
      const fallback = modeToDefaults(config.mode);
      const enableAgent = config.enableAgent ?? fallback.enableAgent;
      const enableVscode = config.enableVscode ?? config.includeVscode ?? fallback.enableVscode;

      const agents = !enableAgent
        ? []
        : (config.agents?.length
          ? config.agents.map((a) => ({
            agentType: a.agentType,
            authType: a.authType,
            apiToken: a.apiToken
          }))
          : (config.agentType
            ? [ {
              agentType: config.agentType,
              authType: config.authType,
              apiToken: config.apiToken
            } ]
            : []));

      const yaml = this.#yamlBuilder.generate({
        serviceName: 'zcp',
        enableAgent,
        enableVscode,
        agents,
        enablePublicAccess: config.enablePublicAccess,
        vscodePassword: config.vscodePassword,
        selectedServices: config.selectedServices
      });

      return firstClassRecipeImport({
        data: {
          projectId,
          serviceImportYaml: yaml,
          recipeSource: ZCP_RECIPE_SOURCE,
          createIntegrationToken: enableAgent,
          // Carry the OAuth-mode agent list so the auth-dialog effect can
          // seed its walker queue at deploy time without reading userData.
          zcpAgents: agents.filter((a) => a.authType === 'oauth').map((a) => a.agentType)
        },
        meta: {
          skipNotification: true,
          source: CONTROL_PLANE_FEATURE_NAME,
          authType: config.authType,
          agentType: config.agentType
        }
      });
    })
  ));

}
